import { createAsyncThunk } from '@reduxjs/toolkit';
import { apiGet, apiPost } from 'api-service/index';
import { mapToApiPlatform, mapToFrontPlatform } from 'utils/mapper';
import { moment } from 'utils/moment';
import { contentAdapter } from 'store/content/adapters';
import { getKioskSearchParamsFromFilters } from 'store/kiosk/kioskSlice';
import {
  bulletinAdapter,
  bulletinTableOfContentsAdaptor,
} from './bulletinContent.adapters';

export const fetchBulletin = createAsyncThunk(
  'bulletinContentReducer/fetchBulletin',
  async ({ bulletinId }, thunkAPI) => {
    try {
      const response = await apiGet(`/bulletin/${bulletinId}`);
      return bulletinAdapter(response.data.result.data);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const fetchBulletinContents = createAsyncThunk(
  'bulletinContentReducer/fetchBulletinContents',
  async (params, thunkAPI) => {
    const wantTagContent =
      thunkAPI.getState().settingsReducer.filters.public.wantTagContent
        ?.selectedValue;

    const { bulletinId, platform = null } = params;

    const finalParams = {};
    if (platform) {
      finalParams.platform = [mapToApiPlatform[platform]];
    } else {
      finalParams.want_republishers = 1;
      finalParams.want_tag_content = wantTagContent ? 1 : 0;
    }

    try {
      const response = await apiGet(
        `/bulletin/${bulletinId}/content`,
        finalParams
      );

      if (platform) {
        return {
          [platform]:
            response.data?.result?.data?.[mapToApiPlatform[platform]]?.map(
              (content) => contentAdapter(content, platform)
            ) || [],
        };
      }

      const contents = {};
      Object.entries(response.data?.result?.data || {}).forEach(
        ([key, value]) => {
          const platform = mapToFrontPlatform[key];
          if (platform) {
            contents[platform] = value?.map((content) =>
              contentAdapter(content, platform)
            );
          }
        }
      );

      return {
        toc: Object.entries(response.data?.result?.data?.toc || {}).reduce(
          (acc, [platform, data]) => {
            acc[platform] = data.map((item, index) => {
              // برای اینکه توی فهرست محتوا بسترهای داخلی معلوم نیست که کدوم بستر هست از محتوا بستر رو برمیداریم
              if (platform === mapToApiPlatform['nativePlatforms']) {
                return bulletinTableOfContentsAdaptor(
                  platform,
                  {
                    ...item,
                    contentPlatform:
                      response.data.result.data?.[
                        mapToApiPlatform['nativePlatforms']
                      ]?.[index]?.platform,
                  },
                  index
                );
              }
              return bulletinTableOfContentsAdaptor(platform, item, index);
            });
            return acc;
          },
          {}
        ),
        contents,
        statistics: response.data.result?.data?.statistics,
      };
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const sortBulletinContent = createAsyncThunk(
  'bulletinContentReducer/sortBulletinContent',
  async (params, thunkAPI) => {
    const { bulletinId, platform } = params;

    const { sort } = thunkAPI.getState().bulletinContentReducer;

    try {
      const response = await apiPost(`/bulletin/${bulletinId}/sorts`, {
        sort_type: 'content',
        platform: mapToApiPlatform[platform],
        sort:
          sort[platform].sortBy.selected === 'defaultSort'
            ? ''
            : sort[platform].sortBy.selected,
        sort_order: sort[platform].sortBy.order,
      });

      await thunkAPI.dispatch(fetchBulletinContents({ bulletinId, platform }));

      return response.data.result.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const downloadPDF = createAsyncThunk(
  'bulletinContentReducer/downloadPDF',
  async (params, thunkAPI) => {
    const { title, html, template } = params;

    const data = new Blob([html], {
      type: 'text/html',
    });

    const formData = new FormData();
    formData.append('title', title);
    formData.append('data', data);

    try {
      const response = await apiPost(
        `/bulletin/v2/export?style=${template}`,
        formData
      );

      return response.data.result.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const fetchBulletinKioskNewspapers = createAsyncThunk(
  'bulletinContentReducer/fetchBulletinKioskNewspapers',
  async ({ createdAt, filters, count, kioskCategories } = {}, thunkAPI) => {
    const exportDate = createdAt ?? new Date();
    const from = moment(exportDate).format('YYYY-MM-DD');
    const to = moment(from).add(1, 'days').format('YYYY-MM-DD');

    const filterParams = kioskCategories?.length
      ? { kiosk_categories: kioskCategories }
      : getKioskSearchParamsFromFilters(filters);

    try {
      const response = await apiGet('/search/api/v1/search', {
        api_path: '/newspaper/kiosk',
        from,
        to,
        count: count || 100,
        ...filterParams,
      });
      return response.data.result.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);
